import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useSiteStore } from '../../store/siteStore';
import { TowerShape, StayWire } from '../../types/site';

interface SiteEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SiteEditModal: React.FC<SiteEditModalProps> = ({ isOpen, onClose }) => {
  const { currentSite, updateSite, setTowerOrientation } = useSiteStore();
  const [siteName, setSiteName] = useState('');
  const [location, setLocation] = useState('');
  const [sections, setSections] = useState(10);
  const [sectionHeight, setSectionHeight] = useState(3);
  const [towerShape, setTowerShape] = useState<TowerShape>('square');
  const [stayWires, setStayWires] = useState<Array<{ height: number; distance: number }>>([]);

  // Initialize form with current site data
  useEffect(() => {
    if (currentSite && isOpen) {
      setSiteName(currentSite.name);
      setLocation(currentSite.location);
      setSectionHeight(currentSite.tower.sectionHeight || 3);
      setSections(Math.round(currentSite.tower.height / (currentSite.tower.sectionHeight || 3)));
      setTowerShape(currentSite.tower.shape || 'square');
      
      // Extract unique stay wire configurations (they're duplicated per corner)
      const uniqueWires = new Map<string, { height: number; distance: number }>();
      currentSite.tower.stayWires?.forEach(wire => {
        const key = `${wire.height}-${wire.distance}`;
        if (!uniqueWires.has(key)) {
          uniqueWires.set(key, { height: wire.height, distance: wire.distance });
        }
      });
      setStayWires(Array.from(uniqueWires.values()));
    }
  }, [currentSite, isOpen]);

  if (!isOpen || !currentSite) return null;

  const towerHeight = sections * sectionHeight;

  const handleClose = () => {
    onClose();
  };

  const generateId = () => {
    return 'xxxx-xxxx-xxxx'.replace(/[x]/g, () => {
      return (Math.random() * 16 | 0).toString(16);
    });
  };

  const addStayWire = () => {
    setStayWires([...stayWires, { height: towerHeight / 2, distance: 10 }]);
  };

  const removeStayWire = (index: number) => {
    setStayWires(stayWires.filter((_, i) => i !== index));
  };

  const updateStayWire = (index: number, field: 'height' | 'distance', value: number) => {
    const updated = [...stayWires];
    updated[index][field] = value;
    setStayWires(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (siteName && location) {
      // Convert stayWires to full StayWire objects with angles
      const numSides = towerShape === 'triangle' ? 3 : 4;
      const fullStayWires: StayWire[] = stayWires.flatMap(wire => {
        const wires: StayWire[] = [];
        for (let i = 0; i < numSides; i++) {
          const angle = towerShape === 'triangle' 
            ? i * 120  // 0°, 120°, 240°
            : i * 90;  // 0°, 90°, 180°, 270°
          wires.push({
            id: generateId(),
            height: wire.height,
            distance: wire.distance,
            angle
          });
        }
        return wires;
      });

      // If shape changed, we need to recalculate mounting points
      // For now, just update basic info. Shape change would require more complex logic
      const updates = {
        name: siteName,
        location,
        tower: {
          ...currentSite.tower,
          height: towerHeight,
          sectionHeight,
          stayWires: fullStayWires,
          // Note: shape change would require recalculating mounting points
          // For now, don't allow shape change in edit mode
        }
      };

      updateSite(currentSite.id, updates);
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 paper-preview-light">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[min(90vh,100%)] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Site</h2>
          <button onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-md text-gray-600">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto modal-scroll">
            <div className="px-4 py-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tower Orientation (°)</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={0}
                max={360}
                value={currentSite.tower.orientationDeg ?? 0}
                onChange={(e) => setTowerOrientation(currentSite.id, Math.max(0, Math.min(360, Number(e.target.value) || 0)))}
                className="w-20 px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              />
              <input
                type="range"
                min={0}
                max={360}
                value={currentSite.tower.orientationDeg ?? 0}
                onChange={(e) => setTowerOrientation(currentSite.id, Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-10 h-10 rounded-full bg-gray-100 border flex items-center justify-center relative">
                <div
                  className="w-0 h-0 border-l-3 border-r-3 border-b-[10px] border-l-transparent border-r-transparent border-b-blue-600 absolute"
                  style={{ transform: `rotate(${currentSite.tower.orientationDeg ?? 0}deg) translateY(-2px)` }}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Site Name
            </label>
            <input
              type="text"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              placeholder="Enter site name"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              placeholder="Enter location"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Tower Shape
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled
                className="px-3 py-1.5 text-sm border-2 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                title="Shape cannot be changed after creation"
              >
                {towerShape === 'square' ? 'Square' : 'Triangle'}
              </button>
              <p className="text-xs text-gray-500 flex items-center">
                Shape cannot be changed
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Number of Sections
            </label>
            <input
              type="number"
              value={sections}
              onChange={(e) => setSections(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              min="1"
              max="100"
            />
            <p className="text-xs text-gray-500 mt-0.5">
              Tower height: <span className="text-gray-600 font-medium">{towerHeight}m</span> ({sections} sections × {sectionHeight}m)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Section Height (meters)
            </label>
            <input
              type="number"
              value={sectionHeight}
              onChange={(e) => setSectionHeight(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              min="1"
              max="10"
              step="0.5"
            />
            <p className="text-xs text-gray-500 mt-0.5">
              Standard towers use 3m sections. Adjust as needed for your design.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-medium text-gray-700">
                Stay Wires (Optional)
              </label>
              <button
                type="button"
                onClick={addStayWire}
                className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus size={14} />
                <span>Add Stay Wire</span>
              </button>
            </div>
            {stayWires.length > 0 && (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {stayWires.map((wire, index) => (
                  <div key={index} className="flex items-center space-x-2 p-1.5 bg-gray-50 rounded-md">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={wire.height}
                        onChange={(e) => updateStayWire(index, 'height', Number(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                        placeholder="Height (m)"
                        min="0"
                        max={towerHeight}
                        step="0.5"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Height (m)</p>
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        value={wire.distance}
                        onChange={(e) => updateStayWire(index, 'distance', Number(e.target.value))}
                        className="w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                        placeholder="Distance (m)"
                        min="1"
                        max="50"
                        step="0.5"
                      />
                      <p className="text-xs text-gray-500 mt-0.5">Distance (m)</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStayWire(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              Stay wires will be added at each corner of the tower at the specified height.
            </p>
          </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex space-x-2 px-4 py-3 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteEditModal;













